import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";

export function Playlists() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["playlists"], queryFn: api.listPlaylists });
  const publicQ = useQuery({ queryKey: ["playlists", "public"], queryFn: api.listPublicPlaylists });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [nameQuery, setNameQueryState] = useState<string>(() => {
    try {
      return new URLSearchParams(window.location.search).get("q") ?? "";
    } catch {
      return "";
    }
  });
  const setNameQuery = (next: string): void => {
    setNameQueryState(next);
    try {
      const u = new URL(window.location.href);
      if (next.trim()) u.searchParams.set("q", next);
      else u.searchParams.delete("q");
      window.history.replaceState(null, "", u.toString());
    } catch {
      /* ignore */
    }
  };

  const create = useMutation({
    mutationFn: () => api.createPlaylist({ name: name.trim(), isPublic }),
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["playlists"] });
      setCreating(false);
      setName("");
      setIsPublic(false);
      window.location.href = `/playlists/${p.id}`;
    },
  });

  const remove = useMutation({
    mutationFn: ({ id }: { id: number }) => api.deletePlaylist(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["playlists"] }),
  });

  if (q.isPending) return <div className="p-8 text-zinc-500">Loading…</div>;
  if (q.isError) return <div className="p-8 text-red-300">Error: {String(q.error)}</div>;
  const playlists = q.data.playlists;
  const needle = nameQuery.trim().toLowerCase();
  const filteredOwned = needle
    ? playlists.filter((p) => p.name.toLowerCase().includes(needle))
    : playlists;
  const publicAll = publicQ.data?.playlists ?? [];
  const filteredPublic = needle
    ? publicAll.filter((p) => p.name.toLowerCase().includes(needle))
    : publicAll;

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Playlists</h2>
        {!creating ? (
          <button
            onClick={() => setCreating(true)}
            className="rounded bg-emerald-700 px-3 py-1.5 text-sm text-emerald-100 hover:bg-emerald-600"
          >
            + New playlist
          </button>
        ) : (
          <div className="flex flex-col items-end gap-1">
            <div className="flex gap-2">
              <input
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Playlist name"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && name.trim().length > 0) create.mutate();
                  if (e.key === "Escape") setCreating(false);
                }}
                className="rounded border border-zinc-700 bg-zinc-800 px-2 py-1 text-sm focus:border-zinc-500 focus:outline-none"
              />
              <button
                onClick={() => create.mutate()}
                disabled={name.trim().length === 0 || create.isPending}
                className="rounded bg-emerald-700 px-3 py-1 text-sm text-emerald-100 hover:bg-emerald-600 disabled:opacity-50"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setCreating(false);
                  setName("");
                  setIsPublic(false);
                }}
                className="rounded bg-zinc-700 px-3 py-1 text-sm hover:bg-zinc-600"
              >
                Cancel
              </button>
            </div>
            <label className="flex cursor-pointer items-center gap-1.5 text-xs text-zinc-400">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              Make public — anyone with library access can view
            </label>
          </div>
        )}
      </div>

      <div className="mb-3">
        <input
          type="search"
          value={nameQuery}
          onChange={(e) => setNameQuery(e.target.value)}
          placeholder="Search playlists by name…"
          className="w-full rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-zinc-200 placeholder:text-zinc-500 focus:border-zinc-500 focus:outline-none"
          aria-label="Search playlists"
        />
      </div>
      {needle && filteredOwned.length === 0 && filteredPublic.length === 0 && (
        <p className="mb-3 text-zinc-500">No playlists match this search.</p>
      )}
      {(filteredOwned.length > 0 || !needle) && (
        <h3 className="mb-2 mt-2 text-sm font-medium uppercase tracking-wide text-zinc-500">
          Your playlists
        </h3>
      )}
      {filteredOwned.length === 0 ? (
        !needle && (
          <p className="text-zinc-500">
            No playlists yet. Create one above, or add items to a new playlist from any media's
            right-click menu.
          </p>
        )
      ) : (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
        >
          {filteredOwned.map((p) => (
            <div
              key={p.id}
              className="group relative overflow-hidden rounded border border-zinc-800 bg-zinc-900 transition-colors hover:border-zinc-700"
            >
              <a href={`/playlists/${p.id}`} className="block">
                <div className="aspect-video bg-zinc-800">
                  {p.cover_media_id != null ? (
                    <img
                      src={`/api/media/${p.cover_media_id}/thumb?size=512`}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-zinc-600">
                      empty
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-medium text-zinc-100">{p.name}</div>
                  {p.is_public && (
                    <span className="mt-0.5 inline-block rounded bg-sky-900/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-sky-200">
                      Public
                    </span>
                  )}
                  <div className="mt-1 flex items-center justify-between text-xs text-zinc-500">
                    <span>
                      {p.item_count} item{p.item_count === 1 ? "" : "s"}
                    </span>
                    <span>{new Date(p.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </a>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  if (window.confirm(`Delete playlist "${p.name}"? Items remain in your library.`)) {
                    remove.mutate({ id: p.id });
                  }
                }}
                className="absolute right-2 top-2 rounded bg-black/60 px-2 py-0.5 text-xs text-rose-200 opacity-0 transition-opacity hover:bg-black/80 group-hover:opacity-100"
                aria-label={`Delete ${p.name}`}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
      {publicAll.length > 0 && (!needle || filteredPublic.length > 0) && (
        <>
          <h3 className="mb-2 mt-8 text-sm font-medium uppercase tracking-wide text-zinc-500">
            Public
          </h3>
          <div
            className="grid gap-3"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
          >
            {filteredPublic.map((p) => (
              <a
                key={p.id}
                href={`/playlists/${p.id}`}
                className="group relative block overflow-hidden rounded border border-zinc-800 bg-zinc-900 transition-colors hover:border-zinc-700"
              >
                <div className="aspect-video bg-zinc-800">
                  {p.cover_media_id != null ? (
                    <img
                      src={`/api/media/${p.cover_media_id}/thumb?size=512`}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-zinc-600">
                      empty
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-medium text-zinc-100">{p.name}</div>
                  <div className="mt-1 flex items-center justify-between text-xs text-zinc-500">
                    <span>by {p.owner_display_name ?? "unknown"}</span>
                    <span>{p.item_count} item{p.item_count === 1 ? "" : "s"}</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
